import { useCallback, useEffect, useRef, useState } from "react";

import SearchField from "~/components/shared/SearchField";
import { Example, useExamples } from "~/components/shared/Examples/useExamples";
import { Skeleton } from "~/components/shared/Skeleton";

import {
  List,
  ListContent,
  ListHeader,
  ListItem,
} from "~/components/shared/StepsEditorSidebar/List";
import { ExampleDetail } from "~/components/shared/StepsEditorSidebar/Examples/ExampleDetail";

import { times } from "lodash";

export function Examples({ examplesPath }: { examplesPath: string }) {
  const { examples, loading, error, query, setQuery } = useExamples(examplesPath);

  const ref = useRef<HTMLDivElement>(null);

  const [selectedExample, setSelectedExample] = useState<Example | null>(null);

  const clearSelectedExample = useCallback(() => {
    setSelectedExample(null);
  }, []);

  useEffect(() => {
    if ((ref.current?.getBoundingClientRect().top ?? 0) < 0) {
      ref.current?.scrollIntoView();
    }
  }, [selectedExample]);

  if (error) {
    return (
      <p className="mt-0">
        There was a problem loading examples, please try reloading the page. If the problem persists
        please contact support.
      </p>
    );
  }

  return (
    <div className="relative flex scroll-mt-3 flex-col gap-3" ref={ref}>
      {selectedExample ? (
        <ExampleDetail example={selectedExample} onClear={clearSelectedExample} />
      ) : (
        <>
          <SearchField
            autofocus={!!query}
            className="-mb-1 [&_input]:h-8"
            onChange={setQuery}
            searching={loading}
            defaultValue={query}
          />

          <List>
            <ListHeader name="Examples" />

            {(() => {
              if (loading && !examples?.length) {
                return (
                  <div className="grid gap-2">
                    {times(9, (index) => (
                      <Skeleton key={index} className="h-[75px] w-full" />
                    ))}
                  </div>
                );
              }

              if (!loading && !examples?.length) {
                return (
                  <p className="text-charcoal-300 m-0">
                    No examples{query && ` matching “${query}”`}.
                  </p>
                );
              }

              return (
                <ListContent>
                  {examples.map((example) => (
                    <ListItem
                      key={example.slug}
                      onClick={() => setSelectedExample(example)}
                      name={example.name}
                      emoji={example.emoji}
                      color={example.color}
                    >
                      <span className="line-clamp-2">{example.description}</span>
                    </ListItem>
                  ))}
                </ListContent>
              );
            })()}
          </List>
        </>
      )}
    </div>
  );
}
